import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sparkles, Menu, X, User, LogOut, Bell } from 'lucide-react';
import { NotificationInbox } from './NotificationInbox';

/**
 * UnifiedHeader - A single header component for ALL pages
 * Handles both logged-in and logged-out states consistently
 * Logo links to /dashboard for logged-in users, / for logged-out users
 */
export default function UnifiedHeader() {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine if current page is active
  const isActive = (path) => location.pathname === path;

  // Logo destination based on auth state
  const logoDestination = isAuthenticated ? '/dashboard' : '/';

  return (
    <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 shadow-sm">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo - Links to dashboard for logged-in, home for logged-out */}
          <Link to={logoDestination} className="flex items-center space-x-2">
            <Sparkles className="w-8 h-8 text-rose-400" />
            <span className="text-2xl font-bold bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">
              VirtualFit
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`font-medium transition-colors ${isActive('/dashboard') ? 'text-rose-500' : 'text-gray-700 hover:text-rose-500'}`}
                >
                  Dashboard
                </Link>
              </>
            ) : (
              <Link 
                to="/" 
                className={`font-medium transition-colors ${isActive('/') ? 'text-rose-500' : 'text-gray-700 hover:text-rose-500'}`}
              >
                Home
              </Link>
            )}
            <Link 
              to="/search" 
              className={`font-medium transition-colors ${isActive('/search') ? 'text-rose-500' : 'text-gray-700 hover:text-rose-500'}`}
            >
              Discover Brands & Influencers
            </Link>
            <Link 
              to="/blog" 
              className={`font-medium transition-colors ${isActive('/blog') ? 'text-rose-500' : 'text-gray-700 hover:text-rose-500'}`}
            >
              Blog
            </Link>
            <Link 
              to="/about" 
              className={`font-medium transition-colors ${isActive('/about') ? 'text-rose-500' : 'text-gray-700 hover:text-rose-500'}`}
            >
              About
            </Link>
          </div>
          
          {/* Right Side Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <div className="text-right mr-2">
                  <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                  <p className="text-xs text-gray-500">
                    {user?.images_used || 0} / {user?.images_limit || 0} images
                  </p>
                </div>
                
                {/* Notification Inbox - Only for brands and influencers */}
                {(user?.user_type === 'brand' || user?.user_type === 'influencer') && (
                    <NotificationInbox />
                )}
                
                <Link 
                  to="/settings" 
                  className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-white/50" 
                  title="Account Settings"
                >
                  <User className="w-5 h-5" />
                </Link>
                <button 
                  onClick={logout} 
                  className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-white/50" 
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-700 hover:text-rose-500 font-medium">Login</Link>
                <Link
                  to="/subscribe"
                  className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  Subscribe Now
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Menu Button + Notification */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Mobile Notification Bell - Only for brands and influencers */}
            {isAuthenticated && (user?.user_type === 'brand' || user?.user_type === 'influencer') && (
              <Link 
                to="/notifications"
                className="p-2 text-gray-600 hover:text-rose-500 rounded-full hover:bg-gray-100 relative"
                data-testid="notification-bell-mobile"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Bell className="w-5 h-5" />
              </Link>
            )}
            <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <div className="flex flex-col space-y-4">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                </>
              ) : (
                <Link to="/" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Home</Link>
              )}
              <Link to="/search" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Discover Brands & Influencers</Link>
              <Link to="/blog" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
              <Link to="/about" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
              
              {isAuthenticated ? (
                <>
                  <Link to="/notifications" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Notifications</Link>
                  <Link to="/settings" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Settings</Link>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-left text-gray-700 hover:text-rose-500 font-medium">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-rose-500 font-medium" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                  <Link to="/subscribe" className="px-6 py-2.5 bg-gradient-to-r from-rose-400 to-orange-400 text-white rounded-full font-semibold text-center" onClick={() => setMobileMenuOpen(false)}>
                    Subscribe Now
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
